import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import ImagesIcon from 'icons/add-image-2.svg';
import AddIcon from 'icons/add-new.svg';
import ChevronDownIcon from 'icons/chevron-down.svg';
import DropPicture from 'icons/drop-picture.svg';
import ImageIcon from 'icons/image.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  useCreateAttachmentMutation,
  useDeleteAttachmentMutation,
  useUpdateAttachmentMutation,
} from 'components/hooks/attachmentsMutation';
import useAttachmentsUploader from 'components/hooks/useAttachmentsUploader';

import FilledButton from 'components/application/buttons/FilledButton';
import OutlinedButton from 'components/application/buttons/OutlinedButton';
import SuccessToast from 'components/application/SuccessToast';

import AttachmentCollection from 'components/attachments/components/AttachmentCollection';
import AttachmentDestroyModal from 'components/attachments/components/AttachmentDestroyModal';
import AttachmentDropZone from 'components/attachments/components/AttachmentDropZone';
import AttachmentPreviewer from 'components/attachments/components/AttachmentPreviewer';
import AttachmentUploadModal from 'components/attachments/components/AttachmentUploadModal';
import { useBreadBoard } from 'components/contexts/Toaster';

import { formatData } from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function Attachments({
  accordionContentContainerClassName,
  addImagesButtonPortalId,
  attachableId,
  attachablePlural,
  attachableType,
  currentAttachments,
  initialIsAttachmentsAccordionOpen = false,
  isReadOnly = false,
  modalContainerSelector,
  onAttachmentDelete,
  onAttachmentInsert,
  onAttachmentUpdate,
  onAttachmentsReorder,
  onTmpAttachmentCreate,
  seedAttachments,
}) {
  const breadBoard = useBreadBoard();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'attachments.components.Attachments',
  });

  const { t: i18nTAttachments } = useTranslation('common', {
    keyPrefix: 'attachments',
  });

  const attachmentsQueryKeyUuid = useMemo(() => uuidv4(), []);

  const [addImagesButtonPortalTarget, setAddImagesButtonPortalTarget] =
    useState(null);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedAttachmentHashId, setSelectedAttachmentHashId] =
    useState(null);

  const [previewedAttachmentHashid, setPreviewedAttachmentHashid] =
    useState(null);

  const [isAttachmentsAccordionOpen, setIsAttachmentsAccordionOpen] = useState(
    initialIsAttachmentsAccordionOpen,
  );

  const createAttachmentMutation = useCreateAttachmentMutation({
    attachableId,
    attachableType,
    attachmentsQueryKeyUuid,
  });

  const updateAttachmentMutation = useUpdateAttachmentMutation({
    attachableId,
    attachableType,
    attachmentsQueryKeyUuid,
  });

  const deleteAttachmentMutation = useDeleteAttachmentMutation({
    attachmentsQueryKeyUuid,
  });

  const { data: domainAttachments } = useQuery({
    initialData: seedAttachments,
    queryKey: ['attachments', attachmentsQueryKeyUuid],
    queryFn: async () => {
      const response = await axios.get(
        `/${attachablePlural}/${attachableId}/attachments`,
      );
      return response.data;
    },
    enabled: !!attachablePlural && !!attachableId,
    select: (data) =>
      formatData({ data, resourceTypes: ['attachment', 'coverImage'] }),
  });

  const domainAttachmentsIndexedByHashId = domainAttachments.data.reduce(
    (acc, attachment) => {
      acc[attachment.attributes.hashid] = attachment;
      return acc;
    },
    {},
  );

  const currentAttachmentsIndexedByHashId = currentAttachments.reduce(
    (acc, attachment) => {
      acc[attachment.hashid] = attachment;
      return acc;
    },
    {},
  );

  const handleSingleFileUpload = (newAttachmentFile) => {
    setIsAttachmentsAccordionOpen(true);
    const tmpId = `tmp-${uuidv4()}`;

    onTmpAttachmentCreate({ tmpId });
    uploadSingleFile([newAttachmentFile], { tmpId });
    handleAttachmentUploadModalClose();
  };

  const { handleFilesSelect: uploadSingleFile } = useAttachmentsUploader({
    onUploaderMutationSuccess: async (uploadKey, file, { tmpId }) => {
      const response = await createAttachmentMutation.mutateAsync({
        fileType: file.type.includes('image') ? 'image' : 'pdf',
        uploadKey,
      });

      const { attributes, id } = response.data;

      onAttachmentUpdate({
        hashid: tmpId,
        props: {
          id,
          hashid: attributes.hashid,
          position: attributes.position,
          displaySizeValue: 1,
          caption: '',
          recordType: attributes.recordType,
        },
      });
    },
    onUploaderMutationError: breadBoard.addInedibleToast,
  });

  const handleSizeSelect = (hashid, size) => {
    const attachment = currentAttachmentsIndexedByHashId[hashid];

    if (attachment.displaySizeValue === size) return;

    onAttachmentUpdate({
      hashid: attachment.hashid,
      props: { displaySizeValue: size },
    });

    if (!attachableId) return; // in case of no attachable, the attachment is updated via params

    updateAttachmentMutation.mutate(
      {
        hashid: attachment.hashid,
        recordType: attachment.recordType,
        params: { display_size: size },
      },
      {
        onError: () => {
          breadBoard.addInedibleToast();
          onAttachmentUpdate({
            hashid: attachment.hashid,
            props: { displaySizeValue: attachment.displaySizeValue },
          });
        },
      },
    );
  };

  const handleCaptionSave = (hashid, caption) => {
    const attachment = currentAttachmentsIndexedByHashId[hashid];

    onAttachmentUpdate({
      hashid: attachment.hashid,
      props: { caption },
    });

    if (!attachableId) return; // in case of no attachable, the attachment is updated via params

    updateAttachmentMutation.mutate(
      {
        hashid: attachment.hashid,
        recordType: attachment.recordType,
        params: { caption },
      },
      {
        onError: () => {
          breadBoard.addInedibleToast();
          onAttachmentUpdate({
            hashid: attachment.hashid,
            props: { caption: attachment.caption },
          });
        },
      },
    );
  };

  const handleRotateSuccess = (hashid) => {
    onAttachmentUpdate({ hashid, props: { hasAttachedChanged: true } });
  };

  const handleDelete = (hashid) => {
    const attachment = currentAttachmentsIndexedByHashId[hashid];
    const attachmentIndex = currentAttachments.findIndex(
      (a) => a.hashid === hashid,
    );

    onAttachmentDelete({ hashid });

    if (!attachableId) return; // in case of no attachable, the attachment is deleted via params

    deleteAttachmentMutation.mutate(
      {
        hashid: attachment.hashid,
        recordType: attachment.recordType,
      },
      {
        onError: () => {
          breadBoard.addInedibleToast();
          onAttachmentInsert({ index: attachmentIndex, props: attachment });
        },
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          breadBoard.addToast(
            <SuccessToast
              message={i18nT('handleDelete.success')}
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        },
      },
    );
  };

  const handleReorder = ({ active, over }) => {
    const attachment = currentAttachmentsIndexedByHashId[active.id];
    const overIndex = currentAttachments.findIndex((a) => a.hashid === over.id);

    onAttachmentsReorder({ active, over });

    if (!attachableId) return; // in case of no attachable, the attachment is updated via params

    updateAttachmentMutation.mutate(
      {
        hashid: active.id,
        recordType: attachment.recordType,
        params: { position: overIndex + 1 },
      },
      {
        onError: () => {
          breadBoard.addInedibleToast();
          onAttachmentsReorder({
            active: over,
            over: active,
          });
        },
      },
    );
  };

  const handleAttachmentUploadModalClose = () => setIsUploadModalOpen(false);

  const handleDeleteButtonClick = (hashid) => {
    setSelectedAttachmentHashId(hashid);
    setIsDeleteModalOpen(true);
  };

  const handleAttachmentsDestroyModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedAttachmentHashId(null);
  };

  const selectedAttachment =
    domainAttachmentsIndexedByHashId[previewedAttachmentHashid];

  const attachmentToPreview =
    selectedAttachment?.attributes?.attachedUrl ?
      selectedAttachment
    : domainAttachments?.included?.attachment &&
      domainAttachments.included.attachment[
        selectedAttachment?.relationships?.original?.data?.id
      ];

  const attachmentActionCallbacks =
    isReadOnly ?
      {}
    : {
        onCaptionSave: handleCaptionSave,
        onDeleteButtonClick: handleDeleteButtonClick,
        onFileUpload: handleSingleFileUpload,
        onReorder: handleReorder,
        onRotateSuccess: handleRotateSuccess,
        onSelectSize: handleSizeSelect,
      };

  const isCollapsable =
    !isReadOnly || (isReadOnly && currentAttachments.length > 0);

  const accordionIsVisible =
    !addImagesButtonPortalTarget || currentAttachments.length > 0;

  useEffect(() => {
    if (addImagesButtonPortalId) {
      setAddImagesButtonPortalTarget(
        document.getElementById(addImagesButtonPortalId),
      );
    }
  }, [addImagesButtonPortalId]);

  return (
    <>
      {addImagesButtonPortalTarget && (
        <PortalledAddImagesButton
          addImagesButtonPortalTarget={addImagesButtonPortalTarget}
          buttonText={i18nTAttachments('addImages')}
          onClick={() => setIsUploadModalOpen(true)}
        />
      )}
      {selectedAttachment && (
        <AttachmentPreviewer
          attachment={attachmentToPreview}
          onClose={() => setPreviewedAttachmentHashid(null)}
        />
      )}

      {isUploadModalOpen && !isReadOnly && (
        <AttachmentUploadModal
          modalContainerSelector={modalContainerSelector}
          onModalClose={handleAttachmentUploadModalClose}
          onUpload={handleSingleFileUpload}
        />
      )}
      {accordionIsVisible && (
        <div
          className={classNames(
            'tw-flex tw-flex-col',
            accordionContentContainerClassName,
          )}
          data-element-name='attachments-accordion-container'
        >
          <div className='tw-flex tw-items-center tw-justify-between'>
            <div
              className={classNames(
                'tw-group tw-flex tw-h-8 tw-items-center tw-space-x-2 tw-text-grey-900',
                isCollapsable && 'tw-cursor-pointer hover:tw-text-grey-600',
              )}
              data-element-name='attachments-accordion'
              onClick={() =>
                isCollapsable &&
                setIsAttachmentsAccordionOpen((prevState) => !prevState)
              }
            >
              <ImageIcon
                className={classNames(
                  'tw-h-5 tw-w-5',
                  isCollapsable && 'group-hover:tw-fill-grey-600',
                )}
              />

              <span className='tw-text-sm'>{i18nT('imagePreview')}</span>
              <div className='tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050'>
                <span className='tw-text-xs tw-font-semibold tw-text-blue-900'>
                  {currentAttachments.length || 0}
                </span>
              </div>
              {isCollapsable && (
                <ChevronDownIcon
                  className={classNames(
                    'tw-h-5 tw-w-5 group-hover:tw-fill-grey-600',
                    !isAttachmentsAccordionOpen && 'tw--rotate-90',
                  )}
                />
              )}
            </div>
            {!isReadOnly && !addImagesButtonPortalTarget && (
              <InlineAddImagesButton
                buttonText={i18nTAttachments('addImages')}
                onClick={() => setIsUploadModalOpen(true)}
              />
            )}
          </div>

          {isAttachmentsAccordionOpen && (
            <div className='tw-mt-6'>
              {currentAttachments.length > 0 ?
                <AttachmentCollection
                  attachmentsQueryKeyUuid={attachmentsQueryKeyUuid}
                  currentAttachments={currentAttachments}
                  domainAttachments={domainAttachments}
                  onPreview={(attachment) => {
                    setPreviewedAttachmentHashid(attachment.attributes.hashid);
                  }}
                  {...attachmentActionCallbacks}
                />
              : <AttachmentDropZone onUpload={handleSingleFileUpload}>
                  <div className='tw-relative tw-flex tw-h-62 tw-min-h-full tw-items-center tw-justify-center tw-rounded-lg tw-border-1 tw-border-dashed tw-border-grey-300 tw-px-3 tw-py-5 tw-text-center'>
                    <div className='tw-mx-6'>
                      <DropPicture />
                      <div className='tw-my-5 tw-text-s tw-tracking-wide tw-text-grey-500'>
                        {i18nT('dropZone.fileTypes')}
                        <br />
                        {i18nT('dropZone.maxFileSize')}
                      </div>
                      <p>{i18nT('dropZone.instructions')}</p>
                    </div>
                  </div>
                </AttachmentDropZone>
              }
            </div>
          )}
        </div>
      )}
      {isDeleteModalOpen && (
        <AttachmentDestroyModal
          additionalContent={
            <p className='tw-mb-0 tw-mt-6'>
              {i18nT('destroyModal.additionalContent')}
            </p>
          }
          confirmationText={i18nT('destroyModal.confirmationText')}
          displayText={i18nT('destroyModal.displayText')}
          modalContainerSelector={modalContainerSelector}
          onClose={handleAttachmentsDestroyModalClose}
          onDestroy={() => {
            handleDelete(selectedAttachmentHashId);
            handleAttachmentsDestroyModalClose();
          }}
        />
      )}
    </>
  );
}

// NOTE: Because Attachments behaviour is being configured from a rails view and not a component tree, we need to define the portalled attachments button here.
function PortalledAddImagesButton({
  addImagesButtonPortalTarget,
  buttonText,
  onClick,
}) {
  const button = (
    <OutlinedButton
      className='tw-w-34'
      color='grey'
      onClick={onClick}
      size='sm'
    >
      <div className='tw-mx-auto tw-flex tw-items-center'>
        <ImagesIcon className='tw-mr-1' height={16} width={16} />
        <span>{buttonText}</span>
      </div>
    </OutlinedButton>
  );

  return (
    addImagesButtonPortalTarget &&
    ReactDOM.createPortal(button, addImagesButtonPortalTarget)
  );
}

function InlineAddImagesButton({ buttonText, onClick }) {
  return (
    <FilledButton className='tw-flex-none' color='mint' onClick={onClick}>
      <div className='tw-flex tw-gap-3'>
        <AddIcon
          className='-tw-ml-1 [&_polygon]:tw-fill-grey-900'
          height={20}
          width={20}
        />
        {buttonText}
      </div>
    </FilledButton>
  );
}

Attachments.propTypes = {
  accordionContentContainerClassName: PropTypes.string,
  addImagesButtonPortalId: PropTypes.string,
  attachableId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  attachablePlural: PropTypes.string,
  attachableType: PropTypes.string,
  currentAttachments: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialIsAttachmentsAccordionOpen: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  modalContainerSelector: PropTypes.string,
  onAttachmentDelete: PropTypes.func.isRequired,
  onAttachmentInsert: PropTypes.func.isRequired,
  onAttachmentUpdate: PropTypes.func.isRequired,
  onAttachmentsReorder: PropTypes.func.isRequired,
  onTmpAttachmentCreate: PropTypes.func.isRequired,
  seedAttachments: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('attachment')).isRequired,
    included: PropTypes.array,
  }),
};
